import { Stack } from '@mui/material';
import AgentPrompts from '../../AgentPrompts';
import { useCallback, useState } from 'react';
import { PromptWithArrowActions } from '../../../common/ai-chat/input-atoms/PromptWithArrow';
import { SinglePromptTitleLayout } from './SinglePromptTitleLayout';
import { useSelector } from 'react-redux';
import { selectSelectedPromptGroup } from '../../../../reducers/promptLibraryReducer';
import { usePrompt } from '../usePrompt';
import { Prompt } from '@server/prompts';
import ConfirmDestructiveDialog from '../../../common/dialogs/ConfirmDestructiveDialog';
import useConfirm from '../../../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import { formatNewlinesForMarkdown } from '../../../../utils/markdownUtils';

interface SinglePromptProps {
  onPromptClick: (prompt: string) => Promise<void>;
}

export const SinglePrompt = ({ onPromptClick }: SinglePromptProps) => {
  const selectedPromptGroup = useSelector(selectSelectedPromptGroup);
  const { iconName, name, description, prompts } = selectedPromptGroup ?? {};
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { selectPrompt, removePrompt, clonePrompt } = usePrompt();
  const { openConfirm, onConfirm, onCancel } = useConfirm();

  const promptClickHandler = async (prompt: string) => {
    setIsSubmitting(true);
    await onPromptClick(formatNewlinesForMarkdown(prompt));
    setIsSubmitting(false);
  };

  const onDeleteClick = useCallback(
    async (promptId: string) => {
      if (!selectedPromptGroup?.id) return; //just for TS

      const isConfirmed = await openConfirm(
        <ConfirmDestructiveDialog
          open
          onConfirm={onConfirm}
          onClose={onCancel}
          title="Are you sure?"
          confirmText="Delete"
          cancelText="Cancel"
        >
          This will permanently remove the prompt and all its details. This action can’t be undone.
        </ConfirmDestructiveDialog>,
      );
      if (isConfirmed) {
        removePrompt(selectedPromptGroup?.id, promptId);
      }
    },
    [selectedPromptGroup?.id, openConfirm, onCancel, onConfirm, removePrompt],
  );

  const onActionClick = useCallback(
    async (action: PromptWithArrowActions, _prompt: Pick<Prompt, 'id' | 'name' | 'promptText'>) => {
      if (!selectedPromptGroup?.id) return; //just for TS
      const prompt = selectedPromptGroup?.prompts?.find((p) => p.id === _prompt.id);
      if (!prompt) return;

      switch (action) {
        case PromptWithArrowActions.Edit:
          selectPrompt(prompt);
          break;
        case PromptWithArrowActions.Delete:
          onDeleteClick(prompt.id);
          break;
        case PromptWithArrowActions.Duplicate:
          await clonePrompt(selectedPromptGroup?.id, prompt);
          break;
      }
    },
    [selectPrompt, onDeleteClick, clonePrompt, selectedPromptGroup],
  );

  return (
    <Stack height="100%">
      <SinglePromptTitleLayout
        iconName={iconName}
        title={name ?? ''}
        description={description}
        groupId={selectedPromptGroup?.id ?? ''}
      />

      <Stack direction="row" alignItems="center" gap={2}>
        <AgentPrompts
          prompts={prompts ?? []}
          onClickPrompt={promptClickHandler}
          onActionClick={onActionClick}
          isDisabled={isSubmitting}
          promptVariant="large"
        />
      </Stack>
    </Stack>
  );
};
